/**
 * Date utility functions for consistent date handling across the application.
 *
 * IMPORTANT: JavaScript's Date constructor interprets date-only strings (e.g., "2026-01-06")
 * as UTC midnight, which can shift the displayed date when converted to local time.
 * These utilities ensure dates display correctly regardless of timezone.
 */

/**
 * Parses a date string and returns a Date object in local time.
 * Handles date-only strings (YYYY-MM-DD) by treating them as local dates, not UTC.
 *
 * @param dateString - Date string in ISO format (YYYY-MM-DD or full ISO datetime)
 * @returns Date object in local time, or null if invalid
 */
export function parseLocalDate(dateString: string | null | undefined): Date | null {
  if (!dateString) {return null;}

  // If it's a date-only string (YYYY-MM-DD), parse as local time
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  // For full datetime strings, parse normally
  return new Date(dateString);
}

/**
 * Formats a date string for display using the user's locale.
 * Correctly handles date-only strings to prevent timezone shifts.
 *
 * @param dateString - Date string in ISO format
 * @param fallback - Value to return if date is invalid (default: '-')
 * @returns Formatted date string (e.g., "1/6/2026")
 */
export function formatDate(dateString: string | null | undefined, fallback: string = '-'): string {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) {return fallback;}
  return date.toLocaleDateString();
}

/**
 * Formats a date string with a specific format.
 *
 * @param dateString - Date string in ISO format
 * @param options - Intl.DateTimeFormat options
 * @param fallback - Value to return if date is invalid
 * @returns Formatted date string
 */
export function formatDateWithOptions(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  fallback: string = '-'
): string {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) {return fallback;}
  return date.toLocaleDateString(undefined, options);
}

/**
 * Calculates age in years from a date of birth string.
 *
 * @param dateOfBirth - Date string in ISO format (YYYY-MM-DD)
 * @returns Age in years, or 0 if invalid
 */
export function calculateAge(dateOfBirth: string | null | undefined): number {
  if (!dateOfBirth) {return 0;}

  const today = new Date();
  const birthDate = parseLocalDate(dateOfBirth);
  if (!birthDate) {return 0;}

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}
